/**
 * InstanceDetail — admin org-wide per-instance drill page.
 * Route: /processes/instances/:id
 * Shows FlowViewer + instance metadata + admin action panel
 * (Cancel / Retry / Reroute).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, Chip, CircularProgress, IconButton,
  Snackbar, Stack, Typography, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Cancel as CancelIcon,
  Replay as RetryIcon,
  CallSplit as RerouteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  processAttachmentsApi, processDefinitionsApi, processInstancesApi, processTasksApi,
} from '../../../api/processes';
import {
  safeAttachmentFilename,
  type ProcessAttachmentMeta,
} from '../../WorkerPortal/ProcessFormResourceValue';
import { submittedFormRows } from '../../WorkerPortal/processFormContract';
import SubmittedApplication, {
  SUBMITTED_LABEL_WIDTH,
} from '../../../components/process/SubmittedApplication';
import { downloadBlob } from '../../../utils/downloadFile';
import { organizationApi } from '../../../api';
import ProcessTimeline from '../../../components/process/ProcessTimeline';
import { submittedOutcome } from '../../../components/worker/WorkerListToolbar';
import FlowViewer from '../../../components/process/FlowViewer';
import {
  getProcessDefinitionDisplay,
  getProcessDefinitionNodeLabel,
} from '../processDisplay';
import InstanceStepList from './InstanceStepList';
import { formatProcessDate } from '../processDisplay';
import { ColleaguePickerSingle } from '../../../components/worker/ColleaguePicker';
import type { MeColleague } from '../../../api/me';
import {
  timelineStepLabel,
  visibleTimelineEvents,
  translateLiveStatus,
  translateTimelineAction,
} from '../processDisplay';

const statusColor = (s: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
  if (s === 'completed') return 'success';
  if (s === 'failed' || s === 'cancelled') return 'error';
  if (s === 'running') return 'warning';
  return 'default';
};

/**
 * ONE label column for this page. `minWidth: 140` was written into every metadata row by hand, and the
 * new sections would have made that six more call sites — the repeated value that drifts (svdy-ui §17).
 * A single constant also keeps the timeline's timestamp column on the same axis as the labels above it,
 * which is what makes the three cards read as one page instead of three.
 */
/**
 * ONE label column for this page, and it is the SAME axis the submitted-application card uses.
 *
 * `minWidth: 140` was written into every metadata row by hand, and the new sections would have made
 * that six more call sites of the same literal (svdy-ui §17). It now takes the shared constant rather
 * than repeating the number: three cards stacked on one page whose label columns differ by a few pixels
 * is exactly the misalignment §15 is about, and two independent 140s drift the moment one is tuned.
 */
const DETAIL_LABEL_WIDTH = SUBMITTED_LABEL_WIDTH;

const DetailRow: React.FC<{
  label: string;
  color?: string;
  children: React.ReactNode;
}> = ({ label, color, children }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start">
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ minWidth: DETAIL_LABEL_WIDTH, flexShrink: 0, fontWeight: 500 }}
    >
      {label}
    </Typography>
    {/* `minWidth: 0` so a long value wraps inside the row instead of forcing the label column wider —
        the address fields on a procurement request are ~200 characters. */}
    <Box sx={{ minWidth: 0, flex: 1 }}>
      {typeof children === 'string' || typeof children === 'number' ? (
        <Typography variant="body2" color={color}>{children}</Typography>
      ) : children}
    </Box>
  </Stack>
);

const InstanceDetail: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [instance, setInstance] = useState<any>(null);
  // The whole definition, not just its name: `system_key` is what tells the display
  // helpers this is a seeded built-in whose name and node labels have translations.
  // Keeping only `.name` is why this page showed the seed's English on three lines.
  const [definition, setDefinition] = useState<{ name: string; system_key?: string | null } | null>(null);
  const [relatedDataError, setRelatedDataError] = useState(false);
  const [tasks, setTasks] = useState<any[]>([]);
  const [workers, setWorkers] = useState<any[]>([]);

  /** worker_id → name, so the step list can name people instead of printing ids. */
  // ONE localizer, used by the flow viewer AND the "current step" line below. Two
  // call sites deriving a label independently is how the viewer came to show the seed's
  // English while every status around it was translated.
  const resolveNodeLabel = useCallback(
    (node: { id?: string; data?: { label?: string } }) => (
      definition ? getProcessDefinitionNodeLabel(t, definition, node) : undefined
    ),
    [definition, t],
  );

  const currentNodeLabel = useMemo(() => {
    const node = instance?.flow_snapshot?.nodes?.find(
      (candidate: any) => candidate.id === instance?.current_node_id,
    );
    if (!node) return null;
    return resolveNodeLabel(node) || node.data?.label || null;
  }, [instance, resolveNodeLabel]);

  const workerNameMap = React.useMemo(() => {    const map = new Map<string, string>();
    for (const w of workers) {
      const name = [w.first_name, w.last_name].filter(Boolean).join(' ') || w.worker_code;
      if (w.id && name) map.set(w.id, name);
    }
    return map;
  }, [workers]);
  const [toast, setToast] = useState<string | null>(null);
  const [rerouteOpen, setRerouteOpen] = useState(false);
  const [rerouteTarget, setRerouteTarget] = useState<MeColleague | null>(null);
  const [rerouteReason, setRerouteReason] = useState('');
  const [rerouting, setRerouting] = useState(false);

  const fetchInstance = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setRelatedDataError(false);
    try {
      const data = await processInstancesApi.get(id);
      setInstance(data);
      try {
        const [taskRows, workerRows, definition] = await Promise.all([
          processTasksApi.list({ instance_id: id }),
          organizationApi.workersApi.list('active'),
          processDefinitionsApi.get(data.definition_id),
        ]);
        setTasks(taskRows as any[]);
        setWorkers(workerRows as any[]);
        setDefinition(definition as { name: string; system_key?: string | null });
      } catch {
        setTasks([]);
        setWorkers([]);
        setDefinition(null);
        setRelatedDataError(true);
      }
    } catch { setInstance(null); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { fetchInstance(); }, [fetchInstance]);

  /**
   * The submitted application, as label/field/value rows.
   *
   * Derived by `submittedFormRows`, shared with the other read surfaces. It used to be inline here
   * WITH an attachment-kind filter, and that filter is the 2026-09-08 regression the owner reported:
   * the `attachments` prop is passed inside this loop, so excluding the field excluded the only row
   * that could ever render the file. The mobile screens it was copied from list attachments
   * separately (`requests.tsx`); this page does not, and neither do `RequestList` or
   * `WorkerRequestDetail` — all three web surfaces now render one way (svdy-ui §20).
   */
  const submittedFields = useMemo(
    () => submittedFormRows(instance?.form_data, instance?.form_schema),
    [instance],
  );

  const handleDownloadAttachment = useCallback(async (attachment: ProcessAttachmentMeta) => {
    // Same admin endpoint the requests modal uses. It streams the bytes same-origin since 2026-09-08;
    // it used to 307 to a presigned S3 URL, which an authenticated XHR cannot follow.
    const blob = await processAttachmentsApi.download(attachment.id);
    downloadBlob(blob, safeAttachmentFilename(attachment.file_name));
  }, []);

  const handleCancel = async () => {
    if (!id) return;
    try {
      await processInstancesApi.cancel(id);
      setToast(t('processes:instances.toastCancelled'));
      fetchInstance();
    } catch { setToast(t('common:feedback.actionFailed')); }
  };

  const handleRetry = async () => {
    if (!id) return;
    try {
      await processInstancesApi.retry(id);
      setToast(t('processes:instances.toastRetried'));
      fetchInstance();
    } catch { setToast(t('common:feedback.actionFailed')); }
  };

  const openReroute = () => {
    setRerouteTarget(null);
    setRerouteReason('');
    setRerouteOpen(true);
  };

  const handleRerouteSubmit = async () => {
    if (!id || !rerouteTarget || !rerouteReason.trim()) return;
    setRerouting(true);
    try {
      await processInstancesApi.reroute(id, {
        new_assignee_id: rerouteTarget.worker_id,
        reason: rerouteReason.trim(),
      });
      setToast(t('processes:instances.toastRerouted', { name: rerouteTarget.name }));
      setRerouteOpen(false);
      fetchInstance();
    } catch (e: any) {
      // Surface the backend's structured error if present
      const detail = e?.detail ?? e?.message;
      const msg = typeof detail === 'string'
        ? detail
        : detail?.message ?? t('common:feedback.actionFailed');
      setToast(msg);
    } finally {
      setRerouting(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>;
  if (!instance) return <Alert severity="error">{t('processes:instanceDetail.notFound')}</Alert>;

  const canCancel = ['running', 'waiting', 'suspended'].includes(instance.status);
  const canRetry = instance.status === 'failed';
  const canReroute = ['running', 'waiting'].includes(instance.status);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, width: '100%' }}>
        <IconButton size="small" onClick={() => navigate('/processes/instances/active')}><BackIcon /></IconButton>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', flex: 1 }}>
          {t('processes:instanceDetail.heading', { number: instance.instance_number || instance.id })}
        </Typography>
        <Chip label={translateLiveStatus(t, instance.status)} size="small" color={statusColor(instance.status)} sx={{ height: 20, fontSize: '0.7rem', borderRadius: 1 }} />
      </Stack>

      {relatedDataError && (
        <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
          {t('processes:instances.loadError')}
        </Alert>
      )}

      {/* The steps, and who has each one — ABOVE the canvas, because this is the question the
          page is opened to answer ("where is my claim and who has it"). The canvas answers a
          different one ("what is the shape of this process"), which an admin asks once, so it
          stays below. */}
      {!relatedDataError && instance.flow_snapshot && (
        <Card variant="outlined" sx={{ width: '100%', mb: 2, borderRadius: 2 }}>
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
              {t('processes:instanceDetail.stepsTitle')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('processes:instanceDetail.stepsDesc')}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <InstanceStepList
                flow={instance.flow_snapshot}
                tasks={tasks as any}
                currentNodeId={instance.current_node_id}
                workerNameMap={workerNameMap}
                formatDate={(v) => formatProcessDate(v ?? '', i18n.language)}
              />
            </Box>
          </Box>
        </Card>
      )}

      {/* Flow Viewer — the branches, for when the list is not enough. */}
      {!relatedDataError && instance.flow_snapshot && (
        <Card variant="outlined" sx={{ width: '100%', mb: 2, p: 1 }}>
          <FlowViewer
            flow={instance.flow_snapshot}
            currentNodeId={instance.current_node_id}
            status={instance.status}
            timeline={instance.timeline}
            currentAssignee={instance.current_assignee}
            resolveNodeLabel={resolveNodeLabel}
          />
        </Card>
      )}

      {/* Admin Actions */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        {canCancel && (
          <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon />} onClick={handleCancel} sx={{ textTransform: 'none', borderRadius: 1 }}>
            {t('processes:instanceDetail.cancelInstance')}
          </Button>
        )}
        {canRetry && (
          <Button size="small" variant="outlined" color="primary" startIcon={<RetryIcon />} onClick={handleRetry} sx={{ textTransform: 'none', borderRadius: 1 }}>
            {t('processes:instanceDetail.retry')}
          </Button>
        )}
        {canReroute && (
          <Button size="small" variant="outlined" color="primary" startIcon={<RerouteIcon />} onClick={openReroute} sx={{ textTransform: 'none', borderRadius: 1 }}>
            {t('processes:instanceDetail.reroute')}
          </Button>
        )}
      </Stack>

      {/* Metadata */}
      <Card variant="outlined" sx={{ width: '100%', p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('processes:instanceDetail.detailsTitle')}</Typography>
        <Stack spacing={0.5}>
          <DetailRow label={t('processes:instanceDetail.definition')}>
            {definition ? getProcessDefinitionDisplay(t, definition).name : '—'}
          </DetailRow>
          <DetailRow label={t('processes:instanceDetail.requestedBy')}>
            {instance.requested_by ? workerNameMap.get(instance.requested_by) ?? '—' : '—'}
          </DetailRow>
          <DetailRow label={t('processes:instanceDetail.currentNode')}>{currentNodeLabel ?? '—'}</DetailRow>
          <DetailRow label={t('processes:instanceDetail.started')}>
            {instance.started_at ? new Date(instance.started_at).toLocaleString(i18n.language) : '—'}
          </DetailRow>
          {instance.error_message && (
            <DetailRow label={t('processes:instanceDetail.error')} color="error.main">
              {instance.error_message}
            </DetailRow>
          )}
        </Stack>
      </Card>

      {/* ── THE APPLICATION THAT WAS SUBMITTED (owner, production, 2026-09-08 → 09-09) ─────────────
          This page showed the flow graph, five metadata rows and a timeline, and never the form or its
          attachments — so an auditor could see WHERE a request sat and not WHAT was asked for. It was not
          a UI omission: `ProcessInstanceResponse` carried none of it, and the detail endpoint now answers
          with `ProcessInstanceDetailResponse`.

          Rendered by the SHARED component, which owns the label column, the row rhythm, the value
          formatter and the title. Before that, five surfaces rendered this one thing five ways — four
          value formatters, three label strategies, three row layouts and three names — which is how the
          admin pages came to print `Estimated Cost 100` where the worker portal printed `100 USD`
          (svdy-ui §20). Absent for an instance started without a request, which the component decides. */}
      {submittedFields.length > 0 && (
        <Card variant="outlined" sx={{ width: '100%', mt: 2, p: 2 }}>
          <SubmittedApplication
            formData={instance.form_data}
            formSchema={instance.form_schema}
            attachments={(instance.attachments ?? []) as ProcessAttachmentMeta[]}
            organizationCurrency={instance.organization_currency}
            onDownload={handleDownloadAttachment}
            t={t}
          />
        </Card>
      )}

      {/* Timeline */}
      {visibleTimelineEvents(instance.timeline as any[], instance.flow_snapshot).length > 0 && (
        <Card variant="outlined" sx={{ width: '100%', mt: 2, p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('processes:instanceDetail.timelineTitle')}</Typography>
          {/* THE SAME PRIMITIVE as the worker portal (2026-09-10). This page rendered a fixed-width
              timestamp COLUMN and no rail, so one concept had two looks — and the owner has been finding
              exactly that all day. The dot-and-rail treatment is the one they chose to standardise on. */}
          <ProcessTimeline
            rows={visibleTimelineEvents(instance.timeline as any[], instance.flow_snapshot)}
            stepName={(nodeId) => timelineStepLabel(instance.flow_snapshot, nodeId, resolveNodeLabel)}
            actionLabel={(action) => translateTimelineAction(t, action)}
            locale={i18n.language}
            terminal={submittedOutcome(instance.status) !== 'in_progress'}
            labels={{
              start: t('common:processTimeline.start'),
              ongoing: t('common:processTimeline.inProgress'),
              end: t('common:processTimeline.end'),
            }}
          />
        </Card>
      )}

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} message={toast} />

      {/* Reroute dialog (svdy-ui §5: form → Dialog, not Drawer) */}
      <Dialog
        open={rerouteOpen}
        onClose={() => !rerouting && setRerouteOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600 }}>
          {t('processes:instanceDetail.rerouteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('processes:instanceDetail.rerouteDialog.body')}
            </Typography>
            <ColleaguePickerSingle
              value={rerouteTarget}
              onChange={setRerouteTarget}
              label={t('processes:instanceDetail.rerouteDialog.newApproverLabel')}
              placeholder={t('processes:instanceDetail.rerouteDialog.newApproverPlaceholder')}
              required
              helperText={t('processes:instanceDetail.rerouteDialog.newApproverHelper')}
              excludeIds={
                instance.current_assignee_id
                  ? [instance.current_assignee_id]
                  : undefined
              }
            />
            <TextField
              label={t('processes:instanceDetail.rerouteDialog.reasonLabel')}
              value={rerouteReason}
              onChange={(e) => setRerouteReason(e.target.value)}
              size="small"
              multiline
              minRows={2}
              required
              fullWidth
              helperText={t('processes:instanceDetail.rerouteDialog.reasonHelper')}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button
            onClick={() => setRerouteOpen(false)}
            disabled={rerouting}
            sx={{ textTransform: 'none' }}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleRerouteSubmit}
            disabled={rerouting || !rerouteTarget || !rerouteReason.trim()}
            sx={{ textTransform: 'none', borderRadius: 1 }}
          >
            {rerouting ? t('processes:instanceDetail.rerouteDialog.submitting') : t('processes:instanceDetail.reroute')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InstanceDetail;
